import { Injectable, SecurityContext } from '@angular/core';

import { LabelService } from 'libraries/service/label/label.service';
import { DurationPipe } from 'libraries/date/duration.pipe';
import { TimeMomentPipe } from 'libraries/date/timeMoment.pipe';
import { Yii } from 'libraries/interfaces/Yii';
import { FILTER_CONFIG } from '../../config/filter.config';
import { IFilterFormValue } from '../../components/quote-filter/quote-filter.component';
import {
    IAirQuoteFilter,
    IExplodedQuoteReference,
    IQuoteQuantity
} from 'libraries/service/resources/airTrip/IAirQuoteFilter';
import { IFilterResult } from './IFilterResult';
import { PricePipe } from 'libraries/pipes/price/price.pipe';
import { DecimalPipe } from "@angular/common";
import { DateService } from "libraries/date/date.service";
import { IAirTripQuoteCurrency } from "libraries/service/resources/airTrip/IGetAirTripQuotes";
import { LanguageService } from "libraries/service/language/language.service";
import {
    EUnitAbbr,
    EWeightUnit,
    ICarbonFootPrint
} from "libraries/component/carbon-footprint/carbon-footprint.interface";
import { DomSanitizer } from '@angular/platform-browser';
import _clone from "lodash-es/clone";
import _cloneDeep from "lodash-es/cloneDeep";

@Injectable()
export class FilterService {

    constructor(
        private readonly durationPipe: DurationPipe,
        private readonly timePipe: TimeMomentPipe,
        private readonly pricePipe: PricePipe,
        private readonly labelService: LabelService,
        private readonly decimalPipe: DecimalPipe,
        private readonly dateService: DateService,
        private readonly languageService: LanguageService,
        private readonly domSanitizer: DomSanitizer,
    ) {
    }

    public filter(filterData: IAirQuoteFilter, filterFormValue: IFilterFormValue, withExplodedQuoteDetail: boolean = false): IFilterResult {
        return Object.keys(filterFormValue)
            .map((controlName: string): IFilterResult => this.getReducedFilterQuotesIds(
                filterData,
                controlName,
                filterFormValue[controlName],
                withExplodedQuoteDetail
            ))
            .reduce(combineFilterResults, {filtered: new Set(), applied: false});
    }

    public getReducedFilterQuotesIds(
        filterData: IAirQuoteFilter,
        controlName: string,
        filterFormValue: string[][] | Array<[number, number]>,
        withExplodedQuoteDetail: boolean = false): IFilterResult {
        if (FILTER_CONFIG[controlName].isBySegment) {
            const filtered: IFilterResult[] = Object.keys(filterData[controlName])
                .map((segmentKey: string, mapIndex: number): IFilterResult => {
                    const index: number = +segmentKey.replace(/\D/g, '') - 1;
                    return getQuoteIdsForFilter(
                        controlName,
                        filterData[controlName][segmentKey],
                        filterFormValue[index],
                        withExplodedQuoteDetail
                    );
                });
            return withExplodedQuoteDetail ?
                filtered.reduce(combineFilterResultsExploded, {filtered: new Set(), applied: false}) :
                filtered.reduce(combineFilterResults, {filtered: new Set(), applied: false});
        }

        return getQuoteIdsForFilter(controlName, filterData[controlName], filterFormValue[0], withExplodedQuoteDetail);
    }

    public getReducedFilterKeys(filterData: IAirQuoteFilter, controlName: string): Map<number, string[]> {
        const filterDataKeys: string[] = Object.keys(filterData[controlName])
            .filter(() => FILTER_CONFIG[controlName]);
        const formatedFilteredData: Map<number, string[]> = FILTER_CONFIG[controlName].isBySegment ?
            filterDataKeys
                .map((key: string): { key: string, index: number } => ({key, index: +key.replace(/\D/g, '') - 1}))
                .sort((a: { key: string, index: number }, b: { key: string, index: number }): number => a.index - b.index)
                .reduce((acc: Map<number, string[]>, {key, index}: { key: string, index: number }) => {
                        const nextValue: string[] = acc.has(index) ? acc.get(index) : [];

                        acc.set(index, [...nextValue, ...Object.keys(filterData[controlName][key])]);

                        return acc;
                    }, new Map<number, string[]>()
                ) : new Map<number, string[]>([[0, filterDataKeys]]);
        return formatedFilteredData;
    }

    public getRangeLabels(labels: string[],
                          controlName: string,
                          [sliderMin, sliderMax]: [number, number],
                          currency?: IAirTripQuoteCurrency,
                          uncompleteRangeLabel?: string
    ): string {
        const rangeValues: string[] = applySlider(labels, [sliderMin, sliderMax]);
        const min: string = rangeValues[0];
        const max: string = rangeValues[rangeValues.length - 1];

        switch (FILTER_CONFIG[controlName].format) {
            case 'duration':
                return this.labelService.completeLabel(
                    uncompleteRangeLabel || Yii.t('wui-iqba-js', 'de %minBoundary% à %maxBoundary%'),
                    {
                        minBoundary: this.durationPipe.transform(min),
                        maxBoundary: this.durationPipe.transform(max)
                    }
                );
            case 'price':
                return this.labelService.completeLabel(
                    uncompleteRangeLabel || Yii.t('wui-iqba-js', 'de %minBoundary% à %maxBoundary%'),
                    {
                        minBoundary: this.domSanitizer.sanitize(SecurityContext.HTML, this.pricePipe.transform(+min, currency ? currency.code : 'EUR')),
                        maxBoundary: this.domSanitizer.sanitize(SecurityContext.HTML, this.pricePipe.transform(+max, currency ? currency.code : 'EUR'))
                    }
                );
            case 'time':
                return this.labelService.completeLabel(
                    uncompleteRangeLabel || Yii.t('wui-iqba-js', 'de %minBoundary% à %maxBoundary%'),
                    {
                        minBoundary: this.timePipe.transform(min, this.dateService.dateFormats.momentTime),
                        maxBoundary: this.timePipe.transform(max, this.dateService.dateFormats.momentTime)
                    }
                );
            case 'carbonFootprint':
                return this.labelService.completeLabel(
                    uncompleteRangeLabel || Yii.t('wui-iqba-js', 'de %minBoundary% à %maxBoundary%'),
                    {
                        minBoundary: this.createCarbonValue(min),
                        maxBoundary: this.createCarbonValue(max)
                    }
                );
            case 'rating':
                const minOutput: string = this.decimalPipe.transform(Number(min) / 100, '1.2-2', this.languageService.userLanguage);
                const maxOutput: string = this.decimalPipe.transform(Number(max) / 100, '1.2-2', this.languageService.userLanguage);
                return this.labelService.completeLabel(
                    uncompleteRangeLabel || Yii.t('wui-iqba-js', 'de %minBoundary%/10 à %maxBoundary%/10'),
                    {
                        minBoundary: minOutput,
                        maxBoundary: maxOutput
                    }
                );
            default:
                break;
        }

        return "";
    }

    private createCarbonValue(value: string): string {
        if (value === "unknown" || value === "0") {
            return Yii.t('wui-shared-js', 'inconnu');
        }

        const carbonValue: ICarbonFootPrint = {
            value: Number(value),
            xUnit: EWeightUnit.Kilogram,
            unitValue: Number(value),
            quantity: 1
        };

        const language: string = Yii && Yii.translate && Yii.translate.config ?
            Yii.translate.config.language :
            'fr';

        return `${this.decimalPipe.transform(carbonValue.value, '1.0-0', language)} ${EUnitAbbr[carbonValue.xUnit]}`;
    }
}

function getCheckboxValues(
    filterField: { [key: string]: IQuoteQuantity },
    filterFormValue: string[],
    withExplodedQuoteDetail: boolean = false): IFilterResult {
    const availableValues: string[] = Object.keys(filterField) || [];

    if (availableValues && filterFormValue && availableValues.length === filterFormValue.length) {
        return {
            applied: false,
            filtered: new Set(),
        };
    }

    const filterValueSet: Set<string> = new Set<string>(filterFormValue);
    return {
        filtered: availableValues
            .filter((filterCriteria: string): boolean => filterValueSet.has(filterCriteria))
            .map((filterCriteria: string): IQuoteQuantity => filterField[filterCriteria])
            .reduce((result: Set<string | string[]>, currentValue: IQuoteQuantity): Set<string | string[]> =>
                    reduceAsSet(result, currentValue, withExplodedQuoteDetail),
                new Set()),
        applied: true,
    };
}

function getSliderValues(
    [sliderMin, sliderMax]: [number, number],
    values: { [key: string]: IQuoteQuantity },
    withExplodedQuoteDetail: boolean = false
): IFilterResult {
    const sliderValues: string[] = Object
        .keys(values)
        .sort((x: string,y: string): number =>  x === 'direct' ? -1 : y === 'direct' ? 1 : 0);

    const selectedValues: string[] = applySlider(sliderValues, [sliderMin, sliderMax]);

    if (selectedValues.length === sliderValues.length) {
        return {
            applied: false,
            filtered: new Set(),
        };
    }

    const propsArray: Set<string> = new Set<string>(selectedValues);

    return {
        filtered: sliderValues
            .filter((val: string): boolean => {
                return propsArray.has(val);
            })
            .map((filterCriteria: string): IQuoteQuantity => values[filterCriteria])
            .reduce((result: Set<string | string[]>, currentValue: IQuoteQuantity): Set<string | string[]> =>
                    reduceAsSet(result, currentValue, withExplodedQuoteDetail),
                new Set()),
        applied: true,
    };
}

function reduceAsSet(result: Set<string | string[]>, values: IQuoteQuantity, withExplodedQuoteDetail: boolean = false): Set<string | string[]> {
    if (!withExplodedQuoteDetail) {
        values.quotes.forEach((id: string): void => {
            result.add(id);
        });
        return result;
    }
    values.exploded.explodedQuoteReferenceArray.forEach((explodedQuoteReference: IExplodedQuoteReference): void => {
        result.add([
            `${explodedQuoteReference.quoteCode}___`,
            ...(explodedQuoteReference.journeyCodeArray.sort((a: string, b: string): number => a.localeCompare(b)))
        ]);
    });
    return result;
}

function applySlider<T>(values: T[], [sliderMin, sliderMax]: [number, number]): T[] {
    const length: number = values.length;

    if (length === 0) {
        return values;
    }

    const minIndex: number = Math.min(length - 1, Math.floor(sliderMin * length / 100));
    const maxIndex: number = Math.ceil(sliderMax * length / 100);

    return values.slice(minIndex, maxIndex + 1);
}

function getQuoteIdsForFilter(
    controlName: string,
    filterField: { [key: string]: IQuoteQuantity },
    value: string[] | [number, number],
    withExplodedQuoteDetail: boolean = false): IFilterResult {
    return FILTER_CONFIG[controlName].type === 'checkbox'
        ? getCheckboxValues(filterField, value as string[], withExplodedQuoteDetail)
        : getSliderValues(value as [number, number], filterField, withExplodedQuoteDetail);
}

function combineFilterResults(result: IFilterResult, current: IFilterResult): IFilterResult {
    if (!current.applied) {
        return result;
    }

    if (!result.applied) {
        return current;
    }

    return {
        applied: true,
        filtered: new Set(
            Array.from(
                result.filtered.values()
            ).filter((id: string): boolean => current.filtered.has(id))
        ),
    };
}

function combineFilterResultsExploded(result: IFilterResult, current: IFilterResult): IFilterResult {

    if (!current.applied) {
        return result;
    }

    if (!result.applied) {
        return current;
    }

    const resultValues: Array<string[] | string> = Array.from(result.filtered.values());
    const currentValues: Array<string[] | string> = Array.from(current.filtered.values());

    const filtered: Set<string | string[]> = new Set();

    const quoteKeys: string[] = resultValues
        //filter on same quote Id
        .filter((resultItem: string[]): boolean => {
            return currentValues.findIndex((data: string[]): boolean => data[0] === resultItem[0]) > -1;
        })
        //extract only quoteIds
        .map(((resultItem: string[]): string => resultItem[0]));

    quoteKeys.forEach((quoteKey: string): void => {
        _cloneDeep(resultValues)
            .filter((item: string[]): boolean => item[0] === quoteKey)
            .forEach((item: string[]): void => {
                const commonQuoteKey: string = item.shift();
                const relatedCurrent: Array<string[] | string> = currentValues
                    .filter((currentItem: string[]): boolean => currentItem[0] === commonQuoteKey);

                _cloneDeep(relatedCurrent)
                    .forEach((relatedCurrentItem: string[]): void => {
                        relatedCurrentItem.shift();
                        filtered.add([commonQuoteKey, ...item, ...relatedCurrentItem]);
                    });

            });
    });
    return {
        applied: true,
        filtered
    };
}
